import { ApplicationRef, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ExternalLinkEnhancer {
  private appRef = inject(ApplicationRef);
  private router = inject(Router);
  private observer: MutationObserver | null = null;

  init(): void {
    // Initial enhancement after app is stable
    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
      )
      .subscribe(() => {
        this.enhance();
        this.observe();
      });

    // Re-run after every navigation
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.appRef.isStable
          .pipe(
            filter((stable) => stable),
            first(),
          )
          .subscribe(() => {
            this.enhance();
            // Optionally disconnect and reconnect observer if needed
          });
      });
  }

  enhance(root: ParentNode = document): void {
    const links =
      root.querySelectorAll<HTMLAnchorElement>('a[target="_blank"]');
    for (const anchor of links) {
      if (!anchor.querySelector('.visually-hidden')) {
        const span = document.createElement('span');
        span.classList.add('visually-hidden');
        span.textContent = ' (opens in new tab)';
        anchor.appendChild(span);
      }
      const rel = new Set(
        (anchor.getAttribute('rel') ?? '').split(' ').filter(Boolean),
      );
      rel.add('noopener');
      rel.add('noreferrer');
      anchor.setAttribute('rel', [...rel].join(' '));
    }
  }

  observe(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement) {
            this.enhance(node);
          }
        }
      }
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}
